import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Plus, Trash, FloppyDisk, Check, X, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import InvoicePurchasing from "../components/invoices/InvoicePurchasing";

const CURRENCIES = ["IDR", "JPY", "KRW", "CNY", "USD", "EUR", "AUD"];

const inp =
    "w-full border border-mist rounded-sm px-3 py-2.5 bg-white text-base focus:outline-none focus:border-sage-dark";
// text-base (16px) is required on iOS to prevent the Safari auto-zoom.

const fmtIDR = (n) =>
    n == null || Number.isNaN(Number(n))
        ? "—"
        : `Rp${Number(n).toLocaleString("id-ID", { maximumFractionDigits: 0 })}`;

const fmtOrig = (n, cur) =>
    n == null || Number.isNaN(Number(n))
        ? "—"
        : `${cur} ${Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

const todayISO = () => {
    // LOCAL date (not UTC) — Jakarta users creating a purchase at 23:00 WIB
    // must still see today's date, not tomorrow (and vice versa).
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

/* =============================================
   PURCHASES — list
   ============================================= */
export default function Purchases() {
    const [sp] = useSearchParams();
    // Legacy purchase batches stay reachable only via ?legacy=1 (hidden from navigation; data and APIs untouched).
    if (sp.get("legacy") === "1") return <PurchaseBatches />;
    return <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 md:py-6 w-full"><InvoicePurchasing /></div>;
}

function PurchaseBatches() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            // Idempotently remove any lingering empty drafts (0 items / Rp0)
            // before showing the list, so stale empties don't accumulate.
            try { await api.post("/purchases/cleanup-empty-drafts"); } catch {}
            const { data } = await api.get("/purchases");
            setRows(data);
        } catch (ex) {
            toast.error("Failed to load purchases");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    return (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10 w-full">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="eyebrow">Owner</div>
                    <h1 className="font-serif text-3xl md:text-4xl">Purchases</h1>
                </div>
                <Link
                    to="/purchases/new"
                    data-testid="purchase-new-btn"
                    className="inline-flex items-center gap-1 bg-sage-dark text-white px-4 py-2.5 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep"
                >
                    <Plus size={14} weight="bold" /> New Purchase
                </Link>
            </div>

            <div className="mt-6 border border-mist rounded-sm bg-white overflow-x-auto no-scrollbar">
                <table className="w-full text-sm" data-testid="purchases-table">
                    <thead className="bg-cream/60 text-neutral-500 uppercase tracking-widest text-xs">
                        <tr>
                            <th className="text-left px-3 py-2.5 whitespace-nowrap">Purchase Date</th>
                            <th className="text-left px-3 py-2.5">Supplier / Store</th>
                            <th className="text-left px-3 py-2.5">Currency</th>
                            <th className="text-right px-3 py-2.5">Items</th>
                            <th className="text-right px-3 py-2.5 whitespace-nowrap">Total Cost (IDR)</th>
                            <th className="text-left px-3 py-2.5">Status</th>
                            <th className="text-left px-3 py-2.5">Created By</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={7} className="px-3 py-5 text-center text-neutral-400">
                                    Loading…
                                </td>
                            </tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr>
                                <td colSpan={7} className="px-3 py-5 text-center text-neutral-400">
                                    No purchases yet. Create your first batch.
                                </td>
                            </tr>
                        )}
                        {rows.map((r) => (
                            <tr
                                key={r.id}
                                onClick={() => (window.location.href = `/purchases/${r.id}`)}
                                data-testid={`purchase-row-${r.id}`}
                                className="border-t border-mist cursor-pointer hover:bg-sage-soft/40"
                            >
                                <td className="px-3 py-2.5 whitespace-nowrap">{r.purchase_date}</td>
                                <td className="px-3 py-2.5">{r.supplier_store || "—"}</td>
                                <td className="px-3 py-2.5">{r.currency}</td>
                                <td className="px-3 py-2.5 text-right">{r.items_count}</td>
                                <td className="px-3 py-2.5 text-right whitespace-nowrap">{fmtIDR(r.total_cost_idr)}</td>
                                <td className="px-3 py-2.5">
                                    <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest ${
                                            r.status === "confirmed"
                                                ? "bg-sage-soft text-sage-deep"
                                                : "bg-amber-100 text-amber-800"
                                        }`}
                                    >
                                        {r.status}
                                    </span>
                                </td>
                                <td className="px-3 py-2.5 text-xs text-neutral-500">
                                    {r.created_by_name || "—"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* =============================================
   PURCHASE NEW — create + bulk paste
   ============================================= */
export function PurchaseNew() {
    const navigate = useNavigate();
    const [header, setHeader] = useState({
        purchase_date: todayISO(),
        supplier_store: "",
        currency: "IDR",
        exchange_rate: 1,
        notes: "",
    });
    const [draft, setDraft] = useState([]); // local preview rows before commit
    const [pasteText, setPasteText] = useState("");
    const [parsing, setParsing] = useState(false);
    const [saving, setSaving] = useState(false);

    const effectiveRate = header.currency === "IDR" ? 1 : Number(header.exchange_rate) || 0;

    const addRow = () => {
        setDraft((rows) => [
            ...rows,
            { sku: "", product_name: "", quantity: 1, unit_cost_original: 0, final_retail_override: null, ok: true },
        ]);
    };
    const removeRow = (idx) => setDraft((rows) => rows.filter((_, i) => i !== idx));
    const editRow = (idx, patch) =>
        setDraft((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

    const parseAndMerge = async () => {
        if (!pasteText.trim()) {
            toast.error("Paste some rows first");
            return;
        }
        setParsing(true);
        try {
            const { data } = await api.post("/purchases/parse-paste", { text: pasteText });
            const merged = (data?.rows || []).map((r) => ({
                sku: r.sku || "",
                product_name: r.product_name || "",
                quantity: r.quantity || 0,
                unit_cost_original: r.unit_cost_original || 0,
                final_retail_override: null,
                ok: r.ok,
                error: r.error,
                raw: r.raw,
            }));
            setDraft((prev) => [...prev, ...merged]);
            setPasteText("");
            toast.success(`Parsed ${merged.length} row(s)`);
        } catch (ex) {
            toast.error("Parse failed");
        } finally {
            setParsing(false);
        }
    };

    const saveDraft = async (then) => {
        if (header.currency !== "IDR" && (!effectiveRate || effectiveRate <= 0)) {
            toast.error("Exchange rate must be > 0");
            return;
        }
        const valid = draft.filter((r) => r.ok && r.sku && r.product_name && r.quantity > 0);
        if (valid.length === 0) {
            toast.error("Add at least one valid item before saving");
            return;
        }
        setSaving(true);
        try {
            const { data: batch } = await api.post("/purchases", {
                purchase_date: header.purchase_date,
                supplier_store: header.supplier_store || null,
                currency: header.currency,
                exchange_rate: effectiveRate,
                notes: header.notes || null,
            });
            if (valid.length) {
                await api.post(`/purchases/${batch.id}/items/bulk`, {
                    items: valid.map((r) => ({
                        sku: r.sku,
                        product_name: r.product_name,
                        quantity: Number(r.quantity),
                        unit_cost_original: Number(r.unit_cost_original),
                    })),
                });
                // Apply any final-retail overrides via individual PATCH calls.
                if (valid.some((r) => r.final_retail_override != null && r.final_retail_override !== "")) {
                    const { data } = await api.get(`/purchases/${batch.id}`);
                    const items = data?.items || [];
                    for (let i = 0; i < valid.length; i++) {
                        const override = valid[i].final_retail_override;
                        if (override != null && override !== "") {
                            const match = items.find((it) => it.sku === valid[i].sku && Number(it.unit_cost_original) === Number(valid[i].unit_cost_original));
                            if (match) {
                                await api.patch(`/purchases/${batch.id}/items/${match.id}`, {
                                    final_retail_price: Number(override),
                                });
                            }
                        }
                    }
                }
            }
            toast.success("Draft saved");
            if (then === "confirm") {
                try {
                    await api.post(`/purchases/${batch.id}/confirm`);
                    toast.success("Purchase confirmed");
                } catch (ex) {
                    toast.error(ex?.response?.data?.detail || "Confirm failed");
                }
            }
            navigate(`/purchases/${batch.id}`);
        } catch (ex) {
            toast.error(ex?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const totalCostIdr = useMemo(() => {
        return draft
            .filter((r) => r.ok)
            .reduce(
                (sum, r) =>
                    sum + Number(r.quantity || 0) * Number(r.unit_cost_original || 0) * effectiveRate,
                0
            );
    }, [draft, effectiveRate]);

    return (
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-10 w-full">
            <Link
                to="/purchases"
                className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-neutral-500 hover:text-ink"
            >
                <ArrowLeft size={14} weight="light" /> Back to purchases
            </Link>
            <h1 className="font-serif text-3xl md:text-4xl mt-2">New Purchase</h1>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 border border-mist bg-white rounded-sm p-4">
                <label className="block">
                    <span className="eyebrow">Purchase Date</span>
                    <input
                        type="date"
                        className={inp}
                        value={header.purchase_date}
                        onChange={(e) => setHeader({ ...header, purchase_date: e.target.value })}
                        data-testid="field-purchase-date"
                    />
                </label>
                <label className="block">
                    <span className="eyebrow">Supplier / Store</span>
                    <input
                        type="text"
                        className={inp}
                        value={header.supplier_store}
                        onChange={(e) => setHeader({ ...header, supplier_store: e.target.value })}
                        data-testid="field-supplier"
                        placeholder="e.g. Tokyo Outlet"
                    />
                </label>
                <label className="block">
                    <span className="eyebrow">Currency</span>
                    <select
                        className={inp}
                        value={header.currency}
                        onChange={(e) =>
                            setHeader({
                                ...header,
                                currency: e.target.value,
                                exchange_rate: e.target.value === "IDR" ? 1 : header.exchange_rate,
                            })
                        }
                        data-testid="field-currency"
                    >
                        {CURRENCIES.map((c) => (
                            <option key={c} value={c}>
                                {c}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="block">
                    <span className="eyebrow">Kurs (1 {header.currency} = X IDR)</span>
                    <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        className={inp}
                        value={header.exchange_rate}
                        onChange={(e) => setHeader({ ...header, exchange_rate: e.target.value })}
                        disabled={header.currency === "IDR"}
                        data-testid="field-exchange-rate"
                    />
                </label>
                <label className="block md:col-span-2">
                    <span className="eyebrow">Notes</span>
                    <textarea
                        className={inp}
                        rows={2}
                        value={header.notes}
                        onChange={(e) => setHeader({ ...header, notes: e.target.value })}
                    />
                </label>
            </div>

            {/* Bulk Paste */}
            <div className="mt-6 border border-mist bg-white rounded-sm p-4">
                <div className="eyebrow">Input Banyak Produk</div>
                <p className="text-xs text-neutral-500 mt-1">
                    Paste tab-separated rows: <code>SKU → Product Name → Qty → Harga Beli / pcs</code>. One row per line. The header row is optional.
                </p>
                <textarea
                    className={`${inp} font-mono text-sm mt-2`}
                    rows={5}
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                    placeholder={"SKU\tProduct Name\tQty\tUnit Cost\nJL071248BM\tRING TSUKIYO SOLI 1CT\t5\t88.15"}
                    data-testid="field-paste"
                />
                <div className="mt-2 flex items-center gap-2">
                    <button
                        type="button"
                        onClick={parseAndMerge}
                        disabled={parsing}
                        data-testid="btn-paste-parse"
                        className="inline-flex items-center gap-1 bg-sage-dark text-white px-3 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep disabled:opacity-50"
                    >
                        {parsing ? "Memproses…" : "Tambahkan Produk"}
                    </button>
                    <button
                        type="button"
                        onClick={addRow}
                        data-testid="btn-add-row"
                        className="inline-flex items-center gap-1 border border-mist px-3 py-2 rounded-sm text-xs uppercase tracking-widest hover:border-sage-dark"
                    >
                        <Plus size={12} weight="bold" /> Tambah Produk Manual
                    </button>
                </div>
            </div>

            {/* Preview Table */}
            <div className="mt-6 border border-mist bg-white rounded-sm overflow-x-auto no-scrollbar">
                <table className="w-full text-sm">
                    <thead className="bg-cream/60 text-neutral-500 uppercase tracking-widest text-xs">
                        <tr>
                            <th className="text-left px-3 py-2.5">SKU</th>
                            <th className="text-left px-3 py-2.5">Product Name</th>
                            <th className="text-right px-3 py-2.5">Qty</th>
                            <th className="text-right px-3 py-2.5">Harga Beli / pcs ({header.currency})</th>
                            <th className="text-right px-3 py-2.5">Total Modal</th>
                            <th className="text-right px-3 py-2.5">Suggested</th>
                            <th className="text-right px-3 py-2.5">Final Retail</th>
                            <th className="text-left px-3 py-2.5 w-16"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {draft.length === 0 && (
                            <tr>
                                <td colSpan={8} className="px-3 py-5 text-center text-neutral-400 text-xs">
                                    Paste rows above or add one manually.
                                </td>
                            </tr>
                        )}
                        {draft.map((r, i) => {
                            const costIdr = (Number(r.unit_cost_original) || 0) * effectiveRate;
                            const suggested = costIdr * 3;
                            return (
                                <tr key={i} className={`border-t border-mist ${r.ok ? "" : "bg-red-50"}`}>
                                    <td className="px-3 py-2">
                                        <input
                                            className={inp}
                                            value={r.sku || ""}
                                            onChange={(e) => editRow(i, { sku: e.target.value, ok: true })}
                                            data-testid={`row-sku-${i}`}
                                        />
                                    </td>
                                    <td className="px-3 py-2">
                                        <input
                                            className={inp}
                                            value={r.product_name || ""}
                                            onChange={(e) => editRow(i, { product_name: e.target.value, ok: true })}
                                            data-testid={`row-name-${i}`}
                                        />
                                    </td>
                                    <td className="px-3 py-2 w-20">
                                        <input
                                            type="number"
                                            inputMode="numeric"
                                            min="1"
                                            className={`${inp} text-right`}
                                            value={r.quantity || 0}
                                            onChange={(e) => editRow(i, { quantity: Number(e.target.value), ok: true })}
                                            data-testid={`row-qty-${i}`}
                                        />
                                    </td>
                                    <td className="px-3 py-2 w-28">
                                        <input
                                            type="number"
                                            inputMode="decimal"
                                            min="0"
                                            step="0.01"
                                            className={`${inp} text-right`}
                                            value={r.unit_cost_original || 0}
                                            onChange={(e) =>
                                                editRow(i, { unit_cost_original: Number(e.target.value), ok: true })
                                            }
                                            data-testid={`row-cost-${i}`}
                                        />
                                    </td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap">{fmtIDR(costIdr)}</td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap">{fmtIDR(suggested)}</td>
                                    <td className="px-3 py-2 w-32">
                                        <input
                                            type="number"
                                            inputMode="numeric"
                                            min="0"
                                            step="1"
                                            className={`${inp} text-right`}
                                            value={r.final_retail_override ?? ""}
                                            onChange={(e) =>
                                                editRow(i, {
                                                    final_retail_override:
                                                        e.target.value === "" ? null : Number(e.target.value),
                                                })
                                            }
                                            placeholder={suggested ? String(Math.round(suggested)) : ""}
                                            data-testid={`row-final-${i}`}
                                        />
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <button
                                            type="button"
                                            onClick={() => removeRow(i)}
                                            className="text-neutral-400 hover:text-red-600 p-1"
                                            data-testid={`row-remove-${i}`}
                                        >
                                            <Trash size={14} weight="light" />
                                        </button>
                                        {!r.ok && (
                                            <div className="text-[10px] text-red-700 mt-1">{r.error}</div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Footer totals + actions */}
            <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm">
                    <span className="eyebrow">Total Cost (IDR)</span>
                    <div className="font-serif text-2xl" data-testid="total-cost-idr">{fmtIDR(totalCostIdr)}</div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => saveDraft("draft")}
                        disabled={saving}
                        data-testid="btn-save-draft"
                        className="inline-flex items-center gap-1 border border-mist px-4 py-2.5 rounded-sm text-xs uppercase tracking-widest hover:border-sage-dark disabled:opacity-50"
                    >
                        <FloppyDisk size={14} weight="light" /> Simpan Draft
                    </button>
                    <button
                        type="button"
                        onClick={() => saveDraft("confirm")}
                        disabled={saving || !draft.some((r) => r.ok)}
                        data-testid="btn-save-confirm"
                        className="inline-flex items-center gap-1 bg-sage-dark text-white px-4 py-2.5 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep disabled:opacity-50"
                    >
                        <Check size={14} weight="bold" /> Simpan Pembelian
                    </button>
                </div>
            </div>
        </div>
    );
}

/* =============================================
   PURCHASE DETAIL — view + edit while draft
   ============================================= */
export function PurchaseDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [batch, setBatch] = useState(null);
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/purchases/${id}`);
            setBatch(data.batch);
            setItems(data.items || []);
        } catch (ex) {
            toast.error("Failed to load purchase");
            navigate("/purchases");
        }
    };
    useEffect(() => {
        load();
    }, [id]);

    const isDraft = batch?.status === "draft";

    const confirmBatch = async () => {
        if (!window.confirm("Confirm this purchase? Items become read-only.")) return;
        setBusy(true);
        try {
            await api.post(`/purchases/${id}/confirm`);
            toast.success("Purchase confirmed");
            load();
        } catch (ex) {
            toast.error(ex?.response?.data?.detail || "Confirm failed");
        } finally {
            setBusy(false);
        }
    };
    const deleteDraft = async () => {
        if (!window.confirm("Delete this draft purchase? This cannot be undone.")) return;
        setBusy(true);
        try {
            await api.delete(`/purchases/${id}`);
            toast.success("Draft deleted");
            navigate("/purchases");
        } catch (ex) {
            toast.error("Delete failed");
        } finally {
            setBusy(false);
        }
    };
    const deleteItem = async (itemId) => {
        if (!window.confirm("Remove this item?")) return;
        try {
            await api.delete(`/purchases/${id}/items/${itemId}`);
            load();
        } catch (ex) {
            toast.error("Remove failed");
        }
    };

    if (!batch) return <div className="p-6">Loading…</div>;

    return (
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-10 w-full">
            <Link
                to="/purchases"
                className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-neutral-500 hover:text-ink"
            >
                <ArrowLeft size={14} weight="light" /> Back to purchases
            </Link>
            <div className="flex items-center justify-between gap-3 flex-wrap mt-2">
                <h1 className="font-serif text-3xl md:text-4xl">
                    Purchase · {batch.purchase_date}
                </h1>
                <span
                    data-testid="batch-status"
                    className={`inline-flex items-center px-3 py-1 rounded-sm text-xs uppercase tracking-widest ${
                        batch.status === "confirmed"
                            ? "bg-sage-soft text-sage-deep"
                            : "bg-amber-100 text-amber-800"
                    }`}
                >
                    {batch.status}
                </span>
            </div>

            <dl className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                <Stat label="Supplier" value={batch.supplier_store || "—"} />
                <Stat label="Currency" value={batch.currency} />
                <Stat label="Kurs" value={batch.exchange_rate} />
                <Stat label="Items" value={batch.items_count} />
                <Stat label="Total (IDR)" value={fmtIDR(batch.total_cost_idr)} />
            </dl>

            {batch.notes && (
                <p className="mt-3 text-sm text-neutral-600" data-testid="batch-notes">
                    {batch.notes}
                </p>
            )}

            <div className="mt-6 border border-mist bg-white rounded-sm overflow-x-auto no-scrollbar">
                <table className="w-full text-sm">
                    <thead className="bg-cream/60 text-neutral-500 uppercase tracking-widest text-xs">
                        <tr>
                            <th className="text-left px-3 py-2.5">SKU</th>
                            <th className="text-left px-3 py-2.5">Product Name</th>
                            <th className="text-right px-3 py-2.5">Qty</th>
                            <th className="text-right px-3 py-2.5">
                                Harga Beli / pcs ({batch.currency})
                            </th>
                            <th className="text-right px-3 py-2.5">Modal / pcs</th>
                            <th className="text-right px-3 py-2.5 bg-sage-soft/60 text-sage-deep">Total Modal</th>
                            <th className="text-right px-3 py-2.5">Suggested</th>
                            <th className="text-right px-3 py-2.5">Final Retail</th>
                            {isDraft && <th className="w-10"></th>}
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr>
                                <td
                                    colSpan={isDraft ? 9 : 8}
                                    className="px-3 py-5 text-center text-neutral-400 text-xs"
                                >
                                    No items on this batch.
                                </td>
                            </tr>
                        )}
                        {items.map((it) => {
                            const lineTotal = Number(it.quantity || 0) * Number(it.unit_cost_idr || 0);
                            return (
                            <tr key={it.id} className="border-t border-mist" data-testid={`detail-row-${it.id}`}>
                                <td className="px-3 py-2.5 font-mono">{it.sku}</td>
                                <td className="px-3 py-2.5">{it.product_name}</td>
                                <td className="px-3 py-2.5 text-right font-sans">{it.quantity}</td>
                                <td className="px-3 py-2.5 text-right whitespace-nowrap text-neutral-600">
                                    {fmtOrig(it.unit_cost_original, it.currency)}
                                </td>
                                <td className="px-3 py-2.5 text-right whitespace-nowrap text-neutral-600">{fmtIDR(it.unit_cost_idr)}</td>
                                <td className="px-3 py-2.5 text-right whitespace-nowrap font-sans font-medium bg-sage-soft/30 text-sage-deep" data-testid={`line-total-${it.id}`}>
                                    {fmtIDR(lineTotal)}
                                </td>
                                <td className="px-3 py-2.5 text-right whitespace-nowrap">{fmtIDR(it.suggested_retail_price)}</td>
                                <td className="px-3 py-2.5 text-right whitespace-nowrap">{fmtIDR(it.final_retail_price)}</td>
                                {isDraft && (
                                    <td className="px-3 py-2.5 text-right">
                                        <button
                                            type="button"
                                            onClick={() => deleteItem(it.id)}
                                            className="text-neutral-400 hover:text-red-600 p-1"
                                        >
                                            <Trash size={14} weight="light" />
                                        </button>
                                    </td>
                                )}
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {isDraft && (
                <div className="mt-6 flex items-center justify-end gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={deleteDraft}
                        disabled={busy}
                        className="inline-flex items-center gap-1 border border-red-200 text-red-700 px-4 py-2.5 rounded-sm text-xs uppercase tracking-widest hover:bg-red-50 disabled:opacity-50"
                        data-testid="btn-delete-draft"
                    >
                        <X size={14} weight="light" /> Delete draft
                    </button>
                    <button
                        type="button"
                        onClick={confirmBatch}
                        disabled={busy || items.length === 0}
                        className="inline-flex items-center gap-1 bg-sage-dark text-white px-4 py-2.5 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep disabled:opacity-50"
                        data-testid="btn-confirm-batch"
                    >
                        <Check size={14} weight="bold" /> Confirm purchase
                    </button>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="border border-mist rounded-sm p-3 bg-white">
            <div className="eyebrow">{label}</div>
            <div className="font-sans mt-1 text-sm" data-testid={`stat-${String(label).toLowerCase().replace(/\s+/g, "-")}`}>
                {value}
            </div>
        </div>
    );
}
